import { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { STORE_SEARCH } from "../../../api/store-queries";
import type {
  StoreSearchQuery,
  StoreSearchQueryVariables,
} from "../../../__generated__/graphql";
import { notifyError } from "../../../utils/toast";
import { AdminSearchListDetailLayout } from "../components/admin-search-list-detail-layout";
import {
  AdminStoreTableWithMobileCards,
  type AdminStoreListItem,
} from "../components/admin-store-table-with-mobile-cards";
type StoreSearchItem = NonNullable<StoreSearchQuery["storeSearch"]>[number];

function toStoreListItem(store: StoreSearchItem): AdminStoreListItem {
  return {
    id: store.id,
    title: store.title,
    img: store.img ?? null,
    address: store.address ?? null,
  };
}

const SOLAPI_MESSAGE_LOG_BASE =
  "https://console.solapi.com/message-log/detail?criteria=to&value=";

const AdminWhatMessageTheyReceive = () => {
  const [keyword, setKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreSearchItem | null>(
    null
  );

  const [search, { data, loading }] = useLazyQuery<
    StoreSearchQuery,
    StoreSearchQueryVariables
  >(STORE_SEARCH);

  const handleSearch = () => {
    const trimmed = keyword.trim();
    if (!trimmed) {
      notifyError("검색어를 입력해주세요.");
      return;
    }
    search({ variables: { keyword: trimmed } });
  };

  const storeList = data?.storeSearch ?? [];
  const listItems: AdminStoreListItem[] = storeList.map(toStoreListItem);

  const handleSelectStore = (store: AdminStoreListItem) => {
    const full = storeList.find((s) => s.id === store.id) ?? null;
    setSelectedStore(full);
  };

  const handleOpenSolapiMessageLog = () => {
    if (!selectedStore) return;
    const contactNumber = selectedStore.seller?.contactNumber ?? "";
    if (!contactNumber) {
      notifyError("해당 매장에 연락처 정보가 없습니다.");
      return;
    }
    window.open(
      `${SOLAPI_MESSAGE_LOG_BASE}${encodeURIComponent(contactNumber)}&cond=eq`,
      "_blank"
    );
  };

  const rightPanelContent = selectedStore ? (
    <div className="space-y-4">
      <p className="text-gray-700">
        선택 매장: <strong>{selectedStore.title}</strong>
      </p>
      <button
        type="button"
        onClick={handleOpenSolapiMessageLog}
        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
      >
        솔라피 메시지 로그 열기
      </button>
    </div>
  ) : null;

  const listContent =
    listItems.length > 0 ? (
      <AdminStoreTableWithMobileCards
        stores={listItems}
        selectionMode="single"
        selectedIds={selectedStore ? [selectedStore.id] : []}
        onSelect={handleSelectStore}
        emptyText="검색 결과가 없습니다."
      />
    ) : null;

  return (
    <AdminSearchListDetailLayout
      title="매장 메시지 확인"
      description="매장을 검색한 뒤 선택하면 솔라피 메시지 로그를 엽니다."
      searchPlaceholder="매장 검색 (이름, 주소, 지역)"
      searchValue={keyword}
      onSearchChange={setKeyword}
      onSearch={handleSearch}
      searchLoading={loading}
      step1Title="1. 매장 검색"
      step2Title="2. 검색 결과에서 선택"
      step3Title="3. 솔라피 메시지 로그"
      listContent={listContent}
      emptyListText="검색어를 입력한 뒤 검색 버튼을 누르거나 Enter를 입력하세요."
      rightPanelContent={rightPanelContent}
      rightPanelEmptyText="왼쪽 목록에서 매장을 선택하세요."
    />
  );
};

export default AdminWhatMessageTheyReceive;
